import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { observer, inject } from 'mobx-react'
import { toJS } from 'mobx'

import { FieldContainer, Title, BulletText, CircleBulletText } from '../styled'
import { isOutdatedInvestment, ownerFirstName } from '../../functions'

// Lists are rebuilt from scratch on each pass; compare by id so an equivalent
// result reuses the existing array instead of triggering another render.
const sameAccounts = (a, b) =>
  a.length === b.length && a.every((account, index) => account.id === b[index].id)

const UpdateInformation = props => {
  const {
    store: { accounts, person, spouse, allPensions, goals },
    getInvestments,
    setInfoNeedsUpdating,
  } = props

  const [outdatedAccounts, setOutdatedAccounts] = useState([])

  const isMounted = useRef(true)

  useEffect(() => {
    isMounted.current = true

    return () => {
      isMounted.current = false
    }
  }, [])

  const shortListAccounts = accounts.filter(
    account => account.type === 'Institutional' || account.type === 'NonGc'
  )
  // The filter above allocates a new array on every render, which would give
  // getAllInvestments a new identity every render and re-run the fetch effect
  // endlessly. Key the memo off the account ids so it only changes when the
  // set of accounts actually does.
  const shortListKey = shortListAccounts.map(account => account.id).join(',')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const accountsToScan = useMemo(() => shortListAccounts, [shortListKey])

  // Only goals with money due out this year that has not been withdrawn yet
  // belong on the to-do list; future-dated goals do not.
  const showGoalWithdrawal = (goals ?? []).some(goal => goal.needsWithdrawalUpdate)

  const showLumpSum = useMemo(() => {
    const pensions = allPensions
      .filter(account => account.paymentType === 'L')
      .filter(account => account.lumpsumreceived === 'N')

    if (person.includeSpouse) {
      return pensions.some(
        account =>
          (account.ownerId === spouse.id && account.pensionStartAge <= spouse.age) ||
          (account.ownerId === person.id && account.pensionStartAge <= person.age)
      )
    }

    // <= rather than an exact age match, so a payout age that passed in an
    // earlier year is still flagged (GC-21652), matching the couple path.
    return pensions.some(account => account.pensionStartAge <= person.age)
  }, [allPensions, person.age, person.id, person.includeSpouse, spouse?.age, spouse?.id])

  const getAllInvestments = useCallback(async () => {
    const staleAccounts = []

    for (const account of accountsToScan) {
      const id = account.id
      let type = account.type

      switch (type) {
        case 'Institutional':
          type = 'institutional'
          break
        case 'NonGc':
          type = 'non-gc'
          break
        default:
          break
      }
      const accountInv = await getInvestments({ type, id })

      // One outdated investment is enough to flag the whole account.
      if (accountInv.some(isOutdatedInvestment)) {
        staleAccounts.push(toJS(account))
      }
    }

    if (!isMounted.current) return

    setOutdatedAccounts(current => (sameAccounts(current, staleAccounts) ? current : staleAccounts))
  }, [getInvestments, accountsToScan])

  useEffect(() => {
    try {
      getAllInvestments()
    } catch (err) {
      console.error(err)
    }
  }, [getAllInvestments])

  const showOutdated = outdatedAccounts.length > 0

  // The to-do page suppresses its "nothing to do" message while this section
  // has anything to show.
  useEffect(() => {
    setInfoNeedsUpdating(showGoalWithdrawal || showLumpSum || showOutdated)
  }, [setInfoNeedsUpdating, showGoalWithdrawal, showLumpSum, showOutdated])

  const showOutdate = () => {
    return (
      <>
        <BulletText>
          Outdated investment balances—locate your most recent statement or log into each of the
          account below to obtain the latest balances, then log in and let us know
        </BulletText>
        {outdatedAccounts.map(acct => {
          const owner = ownerFirstName(acct.participantId, person, spouse)

          return (
            <CircleBulletText key={acct.id}>
              {acct.name}
              {owner && ` (${owner})`}
            </CircleBulletText>
          )
        })}
      </>
    )
  }

  if (!showGoalWithdrawal && !showLumpSum && !showOutdated) return null
  return (
    <>
      <FieldContainer>
        <Title>Update information to clear alerts and to be more certain of results</Title>
        {showGoalWithdrawal && (
          <BulletText>
            Goal withdrawal—when you've withdrawn this year's amount, log in and let us know
          </BulletText>
        )}
        {showLumpSum && (
          <BulletText>
            Lump sum pension payout—when you've received the payout and have deposited it into a
            tax-advantaged account, log in and let us know
          </BulletText>
        )}
        {showOutdated && showOutdate()}
      </FieldContainer>
    </>
  )
}

export default inject('store')(observer(UpdateInformation))
